import { QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { AuthProvider } from '@/auth/AuthProvider.tsx';
import { SessionExpiredDialog } from '@/auth/SessionExpiredDialog.tsx';
import { type SessionExpiredStore, sessionExpiredStore } from '@/auth/session-expired-store.ts';
import type { RuntimeConfig } from '@/lib/types.ts';
import { ConfigProvider } from './config.tsx';
import { createQueryClient } from './query-client.ts';
import { ThemeModeProvider } from './theme.tsx';
import { ToastProvider } from './toast.tsx';

export interface AppProvidersProps {
  config: RuntimeConfig;
  children: ReactNode;
  /** Full-page navigation used by login/logout. Injectable for tests. */
  navigate?: (url: string) => void;
  /** Isolated dialog store (tests). Defaults to the app-wide one. */
  sessionStore?: SessionExpiredStore;
}

export function AppProviders({ config, children, navigate, sessionStore }: AppProvidersProps) {
  const store = sessionStore ?? sessionExpiredStore;
  const [queryClient] = useState(() => createQueryClient({ onUnauthorized: store.open }));

  return (
    <ConfigProvider config={config}>
      <ThemeModeProvider>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider {...(navigate ? { navigate } : {})}>
              {children}
              <SessionExpiredDialog store={store} />
            </AuthProvider>
          </QueryClientProvider>
        </ToastProvider>
      </ThemeModeProvider>
    </ConfigProvider>
  );
}
